import React, {
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';
import Animation from 'components/Animation';
import { Overlay } from 'ncoded-component-library';
import NotificationsContext from 'providers/Notifications/Notifications.context';
import InfiniteScroll from 'components/InfiniteScroll';
import ReportsEmptyState from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/components/ReportsEmptyState';
import NotificationsHeader from 'components/NotificationsHeader';
import useNotifications from 'router/subrouters/Dash/subrouters/hooks/useNotifications';
import LeadNotificationsContext from 'providers/LeadNotifications/LeadNotifications.context';
import useLeadNotifications from 'router/subrouters/Dash/subrouters/hooks/useLeadNotifications';
import { getIsNotificationForLeads } from 'utils';
import NotificationsForLeadsFilters from 'components/NotificationsForLeadsFilters';
import useScrollLock from 'hooks/useScrollLock';

import './NotificationsDrawer.styles.scss';
import './NotificationsDrawer.styles.responsive.scss';

type NotificationsDrawerProps = {
  className?: string;
};

const NotificationsDrawer: React.FC<NotificationsDrawerProps> = (props) => {
  const { className } = props;

  const {
    isDrawerOpen,
    notificationBy,
    notificationGroup,
    notificationStatus,
    setIsDrawerOpen,
    onNotificationsScroll,
  } = useContext(NotificationsContext);
  const {
    onNotificationsScroll: onLeadNotificationsScroll,
    channel,
    status,
    leads,
    deals,
  } = useContext(LeadNotificationsContext);

  const { showEmptyState, placeholders, renderedCards } = useNotifications();

  const {
    showEmptyState: leadShowEmptyState,
    placeholders: leadPlaceholders,
    renderedCards: leadRenderedCards,
  } = useLeadNotifications();

  const isForLeads = getIsNotificationForLeads(notificationBy);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isDrawerOpen) return;

    scrollRef.current?.scrollTo({ top: 0 });
  }, [
    channel,
    status,
    leads,
    deals,
    notificationGroup,
    notificationBy,
    notificationStatus,
    isDrawerOpen,
  ]);

  const shouldShowEmptyState = useMemo(
    () => (isForLeads && leadShowEmptyState) || (!isForLeads && showEmptyState),
    [isForLeads, leadShowEmptyState, showEmptyState],
  );

  const classes = classNames(
    'notifications-drawer',
    {
      'notification-drawer--open': isDrawerOpen,
    },
    className,
  );

  const closeDrawer = useCallback(
    () => setIsDrawerOpen(false),
    [setIsDrawerOpen],
  );

  const { lockScroll, unlockScroll } = useScrollLock();

  useEffect(() => {
    if (isDrawerOpen) lockScroll();

    return () => unlockScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen]);

  return (
    <>
      {isDrawerOpen && (
        <Overlay
          className="notifications-drawer__overlay"
          open
          noContent
          overlayCloses
          lockScroll
          onClose={closeDrawer}
        />
      )}

      <Animation
        open={isDrawerOpen}
        duration={400}
        timingFunction="ease-in-out"
        appearAnimation="moveInRight"
        disappearAnimation="moveOutRight"
      >
        <InfiniteScroll
          localScroll
          className={classes}
          onScroll={
            isForLeads ? onLeadNotificationsScroll : onNotificationsScroll
          }
        >
          <NotificationsHeader showExpand onClose={closeDrawer} />

          <NotificationsForLeadsFilters />

          {shouldShowEmptyState ? (
            <ReportsEmptyState className="notifications-drawer__empty-state" />
          ) : (
            <div ref={scrollRef} className="notifications-drawer__list">
              {isForLeads ? leadRenderedCards : renderedCards}
              {isForLeads ? leadPlaceholders : placeholders}
            </div>
          )}
        </InfiniteScroll>
      </Animation>
    </>
  );
};

export default React.memo(NotificationsDrawer);
